import type {Product, ProductVariant} from '@/product/types';

import type {Image} from '@/product/types';
import type {Option} from '@/cart/types';

/**
 * Utility function for determining if a delivery option is a subscription.
 * Returns true if the option contains a [SellingPlan].
 */
export function isRecurring(deliveryOption: DeliveryOption) {
  return deliveryOption.sellingPlan?.isRecurring ?? false;
}

export function isYearly(deliveryOption: DeliveryOption) {
  if (!isRecurring(deliveryOption)) {
    return false;
  }
  const {intervalUnit, orderIntervalFrequency} = deliveryOption.sellingPlan!;
  return intervalUnit === 'month' && orderIntervalFrequency === 12;
}

function isMonthly(deliveryOption: DeliveryOption) {
  return isRecurring(deliveryOption) && !isYearly(deliveryOption);
}

function isOnetime(deliveryOption: DeliveryOption) {
  return !isRecurring(deliveryOption);
}

/**
 * Utility function for calculating the savings % of a [DeliveryOption].
 * Returns the savings % as a whole number.
 */
export function getSavingsPercent(option: DeliveryOption) {
  const minCompareAtPrice = Math.min(
    ...option.items.map((item) => item.compareAtPrice)
  );
  // TODO: consider whether using the min price is acceptable, or if there is a more nuanced solution available
  const minPrice = Math.min(...option.items.map((item) => item.price));
  const savings = minCompareAtPrice - minPrice;
  return Math.round((savings / minCompareAtPrice) * 100);
}

/**
 * Convenience map for filtering delivery options.
 * Maps a [DeliveryOptionFilter] to a filter function.
 */
const filterFunctionMap: Record<
  DeliveryOptionFilter,
  (option: DeliveryOption) => boolean
> = {
  monthly_subscription: isMonthly,
  yearly_subscription: isYearly,
  one_time_purchase: isOnetime,
};

/**
 * Utility function for determining the default delivery option.
 * Returns the highest value option by default.
 * If a `filterBy` parameter is provided, the options are filtered before returning the highest value option.
 * If no options are found for the `filterBy`, it will be ignored.
 * @param options - An array of [DeliveryOption]s.
 * @param filterBy - An optional [DeliveryOptionFilter].
 * @param filterMap - An optional map of filter functions.
 * @returns The default [DeliveryOption].
 *
 * @example
 * By default, returns the highest value option.
 * ```ts
 * const defaultOption = getDefaultDeliveryOption(options);
 * console.log(defaultOption);
 * // { price: 100, sellingPlan: { intervalUnit: 'month', orderIntervalFrequency: 12 } }
 * ```
 *
 * @example
 * Apply a filter before returning the highest value option.
 * ```ts
 * const defaultOption = getDefaultDeliveryOption(options, 'monthly_subscription');
 * console.log(defaultOption);
 * // { price: 12, sellingPlan: { intervalUnit: 'month', orderIntervalFrequency: 1 } }
 * ```
 *
 * @see [compareValue]
 */
export function getDefaultDeliveryOption(
  options: DeliveryOption[],
  filterBy?: DeliveryOptionFilter,
  filterMap = filterFunctionMap
): DeliveryOption | null {
  let filteredOptions: DeliveryOption[] = [];
  if (filterBy) {
    const filter = filterMap[filterBy] ?? (() => true);
    filteredOptions = options.filter(filter);
  }

  if (filteredOptions.length === 0) {
    filteredOptions = Array.from(options);
  }

  const sortedOptions = filteredOptions.sort(compareValue);
  return sortedOptions[0] ?? null;
}

/**
 * Utility function for comparing two [DeliveryOption]s.
 * Returns a number indicating the sort order.
 * The sort order is determined by the following criteria:
 * 1. Recurring options are preferred over one-time options.
 * 2. For recurring options, the price is sorted high to low.
 * 2. For one-time options, the price is sorted low to high.
 * 3. Frequency interval is sorted low to high.
 *
 * @param a - A [DeliveryOption].
 * @param b - A [DeliveryOption].
 * @returns A number indicating the sort order.
 */
export function compareValue(a: DeliveryOption, b: DeliveryOption) {
  // prefer recurring options
  if (isRecurring(a) && !isRecurring(b)) return -1;
  if (!isRecurring(a) && isRecurring(b)) return 1;

  const priceDiff = comparePrice(a, b);

  // sort frequency interval low to high
  const intervalDiff =
    a.sellingPlan && b.sellingPlan
      ? a.sellingPlan.orderIntervalFrequency -
        b.sellingPlan.orderIntervalFrequency
      : 0;
  return priceDiff || intervalDiff;
}

/**
 * Compares the prices of two delivery options.
 * If either option is recurring, the price is sorted high to low.
 * If both options are one-time, the price is sorted low to high.
 * This way, we can default to the smallest pack size if there are only one-time options.
 * But, we can also default to the annual subscription option if there are recurring options.
 *
 * @param a - The first delivery option.
 * @param b - The second delivery option.
 * @returns The price difference between the two options.
 */
function comparePrice(a: DeliveryOption, b: DeliveryOption) {
  if ([a, b].some(isRecurring)) {
    return minPrice(b) - minPrice(a);
  }
  return minPrice(a) - minPrice(b);

  function minPrice(option: DeliveryOption): number {
    return Math.min(...option.items.map((item) => item.price));
  }
}

/**
 * Utility function for comparing two [DeliveryOption]s.
 * Returns a number indicating the sort order.
 * The sort order is determined by the pack size of the smallest item in the option.
 *
 * @param a - A [DeliveryOption].
 * @param b - A [DeliveryOption].
 * @returns A number indicating the sort order.
 *
 * @example
 * Sort options by the smallest pack size.
 * ```ts
 * const sortedOptions = options.sort(compareMinPackSize);
 * console.log(sortedOptions);
 * // [ { items: [ { packSize: 10 }, { packSize: 20 } ] }, { items: [ { packSize: 5 }, { packSize: 15 } ] } ]
 * ```
 *
 */
export function compareMinPackSize(a: DeliveryOption, b: DeliveryOption) {
  const minPackSizeB = Math.min(...b.items.map((item) => item.packSize));
  const minPackSizeA = Math.min(...a.items.map((item) => item.packSize));
  return minPackSizeA - minPackSizeB;
}

/**
 * Enum for the interval unit of a [SellingPlan].
 * Maps to Recharge's order interval unit values.
 */
export type IntervalUnit = 'week' | 'month' | 'day' | null;

export type DeliveryItem = {
  compareAtPrice: number;
  id: number;
  image: Image | null;
  packSize: number;
  price: number;
  pricePerLoad: number;
  selectedOptions: Option[];
};

export type DeliveryOption = {
  gifts: GiftProduct[];
  items: DeliveryItem[];
  sellingPlan: SellingPlan | null;
};

export type DeliveryOptionFilter =
  | 'monthly_subscription'
  | 'yearly_subscription'
  | 'one_time_purchase';

export type GiftProduct = Pick<Product, 'title' | 'featuredImage'> & {
  variants: Pick<ProductVariant, 'id' | 'price' | 'upsell_compare_at_price'>[];
};

export type DeliveryOptionRecurring = DeliveryOption & {
  sellingPlan: SellingPlan;
};

export type SellingPlan = {
  id: string | null;
  intervalUnit: IntervalUnit;
  isRecurring: boolean;
  orderIntervalFrequency: number;
};
